/**
 * Pin a module-level singleton to `globalThis` so it survives module
 * duplication in the runtime.
 *
 * Why this exists:
 * Module-level `const`s normally identify a single value per module
 * instance. If a consumer's bundle ends up with two copies of
 * `@quero/montilla-core` (peer-version mismatch under pnpm, vendor split
 * chunks, microfrontend boundaries, etc.), each copy gets its own
 * registry / state / SDK instance — and side-effect imports from one
 * copy become invisible to readers using the other copy.
 *
 * That is exactly the failure mode that left `montilla.qeevoEvents`
 * undefined in production when `@quero/qeevo-montilla-plugin` and
 * `@quero/montilla-nuxt` resolved different `montilla-core` versions.
 *
 * Pinning to `globalThis` via `Symbol.for(...)` (which uses the
 * cross-realm symbol registry) makes every copy of this module resolve
 * to the same underlying object reference, so the SDK behaves as a
 * single instance regardless of how many module copies are loaded.
 *
 * Same pattern used by React (`__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED`),
 * Apollo Client, Pinia, and others to defend against the same hazard.
 */
/**
 * Quantas cópias deste módulo — e portanto do core — foram carregadas neste
 * runtime. O `globalSingleton` faz as cópias se comportarem como uma; isto é o
 * que permite CONTAR e avisar, porque duplicação continua sendo bug de bundle:
 * dobra o peso e some com o `"use client"` de quem importa errado.
 *
 * Incrementa no top-level de propósito: top-level de módulo roda uma vez por
 * CÓPIA, então o número conta cópias, não chamadas.
 */
const INSTANCE_KEY = Symbol.for('@quero/montilla-core.instance-count')

;(globalThis as unknown as Record<symbol, number>)[INSTANCE_KEY]
  = ((globalThis as unknown as Record<symbol, number>)[INSTANCE_KEY] ?? 0) + 1

/**
 * Cópias do core neste runtime. `1` é o esperado; mais que isso é o wrapper
 * (`@montilla/nuxt`, `@montilla/next`) avisando em desenvolvimento.
 */
export function getCoreInstanceCount(): number {
  return (globalThis as unknown as Record<symbol, number>)[INSTANCE_KEY] ?? 0
}

export function globalSingleton<T>(key: string, factory: () => T): T {
  const symbol = Symbol.for(`@quero/montilla-core.${key}`)
  const globalScope = globalThis as unknown as Record<symbol, T>
  if (globalScope[symbol] === undefined) {
    globalScope[symbol] = factory()
  }
  return globalScope[symbol]
}

/**
 * Test-only: drop a singleton from `globalThis` so the next
 * `globalSingleton(...)` call recreates it. Used by reset helpers like
 * `__clearPluginRegistryForTests` to give each test a clean slate.
 */
export function __resetGlobalSingletonForTests(key: string): void {
  const symbol = Symbol.for(`@quero/montilla-core.${key}`)
  ;(globalThis as unknown as Record<symbol, unknown>)[symbol] = undefined as unknown
}
